import {
  createContext,
  useContext,
  createSignal,
  createEffect,
  on,
} from 'solid-js';
import { unwrap } from 'solid-js/store';
import { spec, specTrack } from 'framework';
import { schemaValidator } from 'schemas';

const LayerContext = createContext({});
const useLayers = () => useContext(LayerContext);

const LayerProvider = (props) => {
  const [layerInstances, setLayerInstances] = createSignal({});
  const [legendLayers, setLegendLayers] = createSignal([]);
  const [activeLayer, setActiveLayer] = createSignal(null);
  const [visibleLayers, _setVisibleLayers] = createSignal([]);

  createEffect(
    on(
      () => specTrack(props.layers),
      () => {
        if (!props.layers) return;
        let isFetching = true;
        const layerLoader = async (lyrspec) => {
          const layerType = lyrspec.layerSpec.layerType;
          const layermod = await import(
            `../../components/worldlayers/${layerType}/src/index.ts`
          );
          const schema = await import(
            `../../components/worldlayers/${layerType}/schemas/layerspec.json`
          );
          const validate = schemaValidator.compile(schema);
          if (validate(lyrspec.layerSpec)) {
            console.log('Loaded layer ' + lyrspec.id);
            return new layermod.default(lyrspec);
          } else {
            validate.errors.map((error) =>
              console.log(
                `Spec for layer ${lyrspec.id} does not validate: ${error.instancePath} -> ${error.message} [${JSON.stringify(error.params)}]`,
              ),
            );
            return null;
          }
        };
        const layerloads = props.layers.map((spec) =>
          layerLoader(unwrap(spec)),
        );
        const newLayerInstances = {};
        const newVisibleLayers = [];
        const newLegendLayers = [];
        Promise.all(layerloads).then((loaded) => {
          loaded.map((lyrinstance) => {
            if (lyrinstance) {
              newLayerInstances[lyrinstance.id] = lyrinstance;
              if (lyrinstance.visible) newVisibleLayers.push(lyrinstance.id);
              if (lyrinstance.layerSpec.legend)
                newLegendLayers.push(lyrinstance);
            }
          });
          setLayerInstances(newLayerInstances);
          setLegendLayers(newLegendLayers);
          setVisibleLayers(newVisibleLayers);
        });
        return () => {
          isFetching = false;
        };
      },
    ),
  );

  const setVisibleLayers = (requestedVisibleIdsArg) => {
    const requestedIds = [...unwrap(requestedVisibleIdsArg)];
    const visibleCandidateSet = new Set();
    let activeShowWithConstraint = null;

    for (const id of requestedIds) {
      const layer = layerInstances()[id];
      if (!layer) continue;

      const layerShowWith = layer.showWith;

      if (Array.isArray(layerShowWith)) {
        // Current layer is a "Defining Layer".
        // If it's incompatible with a previous constraint, it overrides that context.
        if (
          activeShowWithConstraint !== null &&
          !activeShowWithConstraint.includes(id)
        ) {
          visibleCandidateSet.clear();
        }
        // Add the defining layer itself and its showWith companions.
        visibleCandidateSet.add(id);
        layerShowWith.forEach((swId) => {
          // Only add if the companion layer actually exists
          if (layerInstances()[swId]) {
            visibleCandidateSet.add(swId);
          }
        });
        activeShowWithConstraint = layerShowWith; // Set its showWith as the new constraint.
      } else {
        // Current layer is a "Normal Layer".
        if (activeShowWithConstraint !== null) {
          // A showWith constraint is active.
          if (!activeShowWithConstraint.includes(id)) {
            // This Normal Layer is NOT part of the active constraint. It overrides.
            visibleCandidateSet.clear();
            visibleCandidateSet.add(id);
            activeShowWithConstraint = null; // Context becomes normal.
          } else {
            // This Normal Layer IS part of the active constraint. Add it (ensures it's present if not added by defining layer).
            visibleCandidateSet.add(id);
            // The activeShowWithConstraint remains unchanged.
          }
        } else {
          // No showWith constraint is active (context is normal). Add this Normal Layer.
          visibleCandidateSet.add(id);
        }
      }
    }

    const validAndUniqueIds = Array.from(visibleCandidateSet).filter(
      (vid) => layerInstances()[vid],
    );

    const finalVisibleSet = new Set(validAndUniqueIds);
    let newlyAddedInIteration;
    do {
      newlyAddedInIteration = false;
      for (const [layerId, layerInstance] of Object.entries(layerInstances())) {
        if (
          layerInstance.linked &&
          finalVisibleSet.has(layerInstance.linked) &&
          !finalVisibleSet.has(layerId)
        ) {
          finalVisibleSet.add(layerId);
          newlyAddedInIteration = true;
        }
      }
    } while (newlyAddedInIteration);

    _setVisibleLayers(Array.from(finalVisibleSet));
  };

  const zoomVisibility = (id, zoom) => {
    const layer = layerInstances()[id];
    const hidden = zoom < layer.minZoom || zoom > layer.maxZoom;
    return !hidden;
  };

  return (
    <LayerContext.Provider
      value={{
        layerInstances,
        legendLayers,
        activeLayer,
        setActiveLayer,
        visibleLayers,
        setVisibleLayers,
        zoomVisibility,
      }}
    >
      {props.children}
    </LayerContext.Provider>
  );
};

export { useLayers, LayerProvider };
